import React from "react";
import { useParams, Link } from "react-router-dom";
import RentalSearch from "./RentalSearch"; // Adjust path as needed
import heroImg from "../../assets/new-york.jpg"; // Adjust path as needed

// --- SVG Icon Helpers ---
const CheckIcon = ({ className = "w-4 h-4" }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

const PhoneIcon = ({ className = "w-4 h-4" }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
    />
  </svg>
);

const LocationIcon = ({ className = "w-4 h-4" }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.243-4.243a8 8 0 1111.314 0z"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
    />
  </svg>
);

export default function LocationRentalPage() {
  const { locationId } = useParams();

  // Parse location ID (e.g., "airport-atl" or "city-miami-fl") to a readable name
  const formatLocationName = (id) => {
    if (!id) return "Location";

    if (id.startsWith("airport-")) {
      return id.replace("airport-", "").toUpperCase() + " Airport";
    }

    if (id.startsWith("city-")) {
      const parts = id.replace("city-", "").split("-");
      const state = parts.pop().toUpperCase();
      const city = parts
        .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
        .join(" ");
      return `${city}, ${state}`;
    }

    return id;
  };

  const locationName = formatLocationName(locationId);
  const isAirport = locationId?.startsWith("airport-");

  const handlePhoneClick = () => {
    if (window.gtag) {
      window.gtag("config", "AW-18343552397/3FAKCIr6l9ccEI3L8apE", {
        phone_conversion_number: "1-877-991-0909",
      });
    }
  };

  return (
    <div className="bg-white pb-20 font-sans text-slate-800 overflow-x-hidden">
      {/* ================= HERO SECTION ================= */}
      <section className="mx-auto max-w-[1580px] px-4 py-8 md:px-8 lg:py-16 xl:py-20">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:items-center lg:gap-16">
          <div className="max-w-xl mx-auto lg:mx-0 w-full">
            <p className="mb-3 md:mb-4 flex items-center text-[10px] md:text-[11px] font-extrabold uppercase tracking-widest text-[#e8211b]">
              <span className="mr-2 text-[14px] leading-none">•</span>{" "}
              {isAirport ? "AIRPORT" : "CITY"} RENTAL ASSISTANCE
            </p>
            <h1 className="mb-4 md:mb-6 text-4xl sm:text-5xl font-black leading-[1.1] text-[#081c3a] md:text-6xl lg:text-[64px]">
              Car Rentals in {locationName}
            </h1>
            <p className="mb-6 md:mb-8 text-[15px] sm:text-[17px] leading-relaxed text-[#3a4759]">
              Request rental quote assistance for {locationName}, comparing
              options from top providers for{" "}
              {isAirport
                ? "immediate airport pickup"
                : "convenient downtown access"}
              , business travel, and family trips.
            </p>

            <div className="mb-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
              <a
                href="#rental-request"
                className="text-center rounded-full bg-[#e8211b] px-6 py-3.5 md:py-4 text-[13px] font-extrabold uppercase tracking-wide text-white transition-colors hover:bg-red-700 shadow-[0_8px_20px_rgba(232,33,27,0.25)]"
              >
                Compare Rental Options
              </a>
              <a
                href="tel:+18779910909"
                onClick={handlePhoneClick}
                className="flex justify-center items-center gap-2 rounded-full border border-slate-200 bg-white px-6 py-3.5 md:py-4 text-[13px] font-extrabold uppercase tracking-wide text-[#081c3a] transition-all hover:border-[#081c3a] hover:bg-slate-50 shadow-sm"
              >
                <PhoneIcon className="h-4 w-4" /> Call 1-877-991-0909
              </a>
            </div>

            <div className="flex flex-wrap gap-2 md:gap-3">
              {[
                "Best Local Rates",
                "Wide Vehicle Selection",
                "24/7 Support Support",
              ].map((feat) => (
                <div
                  key={feat}
                  className="flex items-center gap-2 rounded-full border border-slate-100 bg-white px-3 py-2 sm:px-4 sm:py-2.5 text-xs sm:text-sm font-bold text-[#081c3a] shadow-sm"
                >
                  <CheckIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-[#e8211b]" />
                  {feat}
                </div>
              ))}
            </div>
          </div>

          <div className="relative w-full mt-6 lg:mt-0">
            <div className="hidden lg:block absolute -left-6 top-10 h-64 w-24 rounded-[40px] bg-red-50 opacity-50 blur-xl"></div>
            <div className="relative overflow-hidden rounded-2xl md:rounded-[32px] shadow-xl md:shadow-2xl">
              <img
                src={heroImg}
                alt={`Rental in ${locationName}`}
                className="h-[350px] sm:h-[400px] lg:h-[500px] w-full object-cover"
              />
              <div className="absolute bottom-4 left-4 right-4 sm:right-auto sm:bottom-6 sm:left-6 sm:max-w-[320px] rounded-xl sm:rounded-2xl bg-white/95 backdrop-blur-sm sm:bg-white p-4 sm:p-6 shadow-xl">
                <p className="mb-1 sm:mb-2 text-[9px] sm:text-[10px] font-extrabold uppercase tracking-widest text-[#e8211b]">
                  RESERVATION SUPPORT
                </p>
                <p className="text-[14px] sm:text-[16px] lg:text-[17px] font-extrabold leading-tight text-[#081c3a]">
                  Modify pickups, extend rentals, and compare local availability
                  effortlessly.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="bg-slate-100 p-5">
        <RentalSearch />
      </div>

      {/* ================= DETAILS SECTION ================= */}
      <section className="mx-auto max-w-[1580px] px-4 md:px-8 mt-8 lg:mt-12">
        <div className="mx-auto max-w-5xl">
          <p className="mb-2 md:mb-3 text-[10px] md:text-[11px] font-extrabold uppercase tracking-widest text-[#e8211b]">
            {locationName.toUpperCase()} RENTAL GUIDE
          </p>
          <h2 className="mb-4 md:mb-6 text-2xl sm:text-3xl font-extrabold leading-tight text-[#081c3a] md:text-[34px]">
            Compare Car Rental Options in {locationName}
          </h2>

          <div className="mb-6 md:mb-8 space-y-3 md:space-y-4 text-[14px] sm:text-[15px] leading-relaxed text-[#3a4759]">
            <p>
              Traveling to <strong>{locationName}</strong>? Whether you need an
              economy car for city navigation, a full-size SUV for a family road
              trip, or a luxury vehicle for business meetings, comparing local
              options ensures you get the right vehicle for your needs.
            </p>
            <p>
              We provide independent booking assistance for new rental
              inquiries. We are not affiliated with, endorsed by, or acting on
              behalf of any specific airport, airline, or rental company.
            </p>
          </div>

          <div className="mb-10 md:mb-12 flex flex-wrap gap-2">
            {[
              `${locationName} rentals`,
              isAirport ? "Terminal Pickup" : "Downtown Pickup",
              "Business rentals",
              "Family trips",
              "SUV requests",
            ].map((tag, idx) => (
              <span
                key={idx}
                className="flex items-center gap-1.5 md:gap-2 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 md:px-3 md:py-1.5 text-[12px] md:text-[13px] font-bold text-[#081c3a] shadow-sm"
              >
                <LocationIcon className="h-3 w-3 md:h-3.5 md:w-3.5 text-slate-400" />{" "}
                {tag}
              </span>
            ))}
          </div>

          <h3 className="mb-4 md:mb-5 text-xl md:text-[22px] font-extrabold text-[#081c3a]">
            Why rent a car in {locationName}?
          </h3>
          <div className="mb-10 md:mb-12 grid grid-cols-1 gap-3 sm:gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[
              {
                title: "Freedom to Explore",
                desc: "Skip the ride-shares and public transit schedules. A rental car gives you total control over your itinerary.",
              },
              {
                title: "Vehicle Fit Guidance",
                desc: "Compare compact cars for easy parking, SUVs for extra luggage, and premium sedans for comfort.",
              },
              {
                title: "Flexible Durations",
                desc: "Whether you need a car for a weekend getaway or a month-long business trip, find the terms that work for you.",
              },
            ].map((card, idx) => (
              <div
                key={idx}
                className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5 shadow-sm"
              >
                <div className="mb-2 md:mb-3 flex items-start sm:items-center gap-2 text-[#081c3a]">
                  <LocationIcon className="mt-0.5 sm:mt-0 h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
                  <h4 className="text-[14px] sm:text-[15px] font-bold">
                    {card.title}
                  </h4>
                </div>
                <p className="text-[12px] sm:text-[13px] leading-relaxed text-[#4b6a97]">
                  {card.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
